import type { CirclesVariant, Variant } from '../gameTypes.js';
import type {
  CircleHitObject,
  CirclesChart,
  HitSampleSet,
  KiaiSection,
} from '../circlesChart.js';
import {
  OSU_PLAYFIELD_HEIGHT,
  OSU_PLAYFIELD_WIDTH,
} from '../circlesChart.js';
import type { ChartAudio, ChartPalette } from '../chart.js';
import { buildVariantTitle } from '../chart.js';
import {
  GOOD_WINDOW_MS,
  OK_WINDOW_MS,
  PERFECT_WINDOW_MS,
  type Difficulty,
} from '../gameTypes.js';
import { buildCirclesVariantSlug } from '../gameTypes.js';
import {
  variantSelectBeats,
  type RawOnsetsFile,
  type SelectBeatsOpts,
} from '../builtinPulseMaps/audioFromOnsets.js';

/**
 * Per-variant tuning for the Circles renderer/engine. These knobs
 * define how visually demanding each variant is (faster approach,
 * smaller circles, tighter timing windows → harder to click in time)
 * on top of the variant-driven density that {@link variantSelectBeats}
 * already applies via the underlying onset filter.
 *
 * `approachMs` is the wall-clock reading window in milliseconds —
 * matches osu!'s AR semantics (BPM-independent, so muscle memory
 * transfers between songs). Values sit ~20-30% friendlier than the
 * canonical osu equivalents.
 *
 * The circles ladder runs four tiers. `normal` and `hard` are the same
 * source beatmap (the imported osu! easy/normal diff) at the same calm
 * reading speed — `normal` strips every slider to a single click so a
 * brand-new player learns aim+timing first, then `hard` reintroduces
 * the sliders. `extreme` / `insane` step up speed + density (the
 * imported hard / hardest diffs):
 *
 *   - Normal  1320 ms (≈ AR4)   — slider-free on-ramp
 *   - Hard    1320 ms (≈ AR4)   — same map, sliders restored
 *   - Extreme  950 ms (≈ AR6.7) — between osu Normal and Hard
 *   - Insane   720 ms (≈ AR8.2) — just shy of osu Insane
 *
 * (osu's preempt formula: AR<5 → 1200 + 120·(5-AR); AR>5 → 1200 - 150·(AR-5).
 * AR5 = 1200 ms, AR8 = 800 ms, AR9 = 600 ms.)
 *
 * `circleRadius` is in osu playfield units (0..512 wide). osu's
 * "Circle Size" mapping: CS3 ≈ 50, CS5 ≈ 38, CS7 ≈ 27. Our defaults
 * sit near the equivalent osu CS — slightly smaller than the
 * original Circles tuning so circles read as discrete targets rather
 * than overlapping blobs at dense stream positions, and so edge
 * circles in imported osu charts (whose positions are squeezed
 * inward by `importedOsuChart.ts`) clip less of their body into the
 * playfield border. Applies globally: synthesised charts get the
 * same trim, which is a minor difficulty bump rather than a fix —
 * `PLAYFIELD_PAD` already keeps their hits well clear of the frame.
 *
 * `windowScale` multiplies ALL THREE timing tiers (perfect / good /
 * ok) uniformly. Normal keeps the Pulse-equivalent forgiveness; Hard
 * and Extreme tighten progressively so harder maps demand precision
 * proportional to their faster approach speeds. Scaling the OK tier
 * is essential — leaving it at the global default would mean Extreme
 * has Insane-level perfect windows but beginner-level "save" mercy,
 * which makes the felt difficulty curve mushy.
 */
export const CIRCLES_VARIANT_TUNING: Record<
  CirclesVariant,
  { approachMs: number; circleRadius: number; windowScale: number }
> = {
  normal:  { approachMs: 1320, circleRadius: 46, windowScale: 1.0 },
  hard:    { approachMs: 1320, circleRadius: 46, windowScale: 1.0 },
  extreme: { approachMs:  950, circleRadius: 40, windowScale: 0.85 },
  insane:  { approachMs:  720, circleRadius: 33, windowScale: 0.7 },
};

/**
 * How many circles share a combo-colour group before we cycle to the
 * next colour. osu! source maps drive new combos manually; for our
 * synthesised charts we approximate with a fixed run length plus an
 * automatic break on big rhythm gaps (treated as a phrase boundary).
 *
 * Each phrase rotates through 4 combo colours so the rendered playfield
 * has visual variety without a stutter every other circle.
 */
const COMBO_LENGTH = 5;
const COMBO_PALETTE_SIZE = 4;

/**
 * Beats of silence required to force a new combo. A long gap signals a
 * new phrase even if the COMBO_LENGTH counter hasn't ticked over yet,
 * which keeps the combo grouping feeling musical.
 */
const COMBO_GAP_BEATS = 2.0;

/**
 * Inset from the playfield edges so the renderer can still draw the
 * approach ring around an edge-most circle without clipping. ~64 units
 * is roughly 1.2× the largest hit-circle radius.
 */
const PLAYFIELD_PAD = 64;

/**
 * Per-section pattern parameters. The synthesised flow uses these to
 * steer angle and step distance — verses are tight + flowing, choruses
 * are jumpier, drops are wide and aggressive. Adapter outputs feel
 * loosely in line with how a human osu! mapper would shape each section.
 */
interface SectionPattern {
  /** Mean step distance in osu units. */
  stepBase: number;
  /** Multiplied per beat-gap so cluster taps stay close, lone taps fly. */
  stepGapScale: number;
  /** Mean angle change per circle (radians). */
  angleDelta: number;
  /** Stochastic angle wobble (radians). */
  angleJitter: number;
}

const SECTION_PATTERNS: Record<string, SectionPattern> = {
  verse:   { stepBase: 70,  stepGapScale: 18, angleDelta: 1.05, angleJitter: 0.4 },
  chorus:  { stepBase: 110, stepGapScale: 24, angleDelta: 1.45, angleJitter: 0.6 },
  bridge:  { stepBase: 90,  stepGapScale: 20, angleDelta: 0.85, angleJitter: 0.5 },
  drop:    { stepBase: 145, stepGapScale: 30, angleDelta: 1.65, angleJitter: 0.75 },
  default: { stepBase: 95,  stepGapScale: 22, angleDelta: 1.20, angleJitter: 0.55 },
};

export interface CircleSectionMarker {
  startBeat: number;
  endBeat: number;
  /** Pattern key — keys mirror Pulse's {@link SectionKind} for shared muscle memory. */
  kind: 'verse' | 'chorus' | 'bridge' | 'drop';
}

/**
 * Find the section containing `beat`. Returns the default pattern when
 * no section overlaps (e.g. an intro pause before the first section).
 */
function patternForBeat(
  sections: ReadonlyArray<CircleSectionMarker>,
  beat: number,
): SectionPattern {
  for (const s of sections) {
    if (beat >= s.startBeat && beat < s.endBeat) {
      return SECTION_PATTERNS[s.kind] ?? SECTION_PATTERNS.default;
    }
  }
  return SECTION_PATTERNS.default;
}

function clamp(value: number, min: number, max: number): number {
  if (value < min) {
    return min;
  }
  if (value > max) {
    return max;
  }
  return value;
}

/**
 * Lightweight deterministic PRNG. We seed it from the chart slug so the
 * generated hit-object positions are stable across builds — the same
 * input chart always produces the same circles layout, which keeps the
 * server-side replay verifier (Phase 7) free of "random regenerated"
 * mismatches.
 */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function hashSeed(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

export interface LayoutHitObjectsOpts {
  beats: ReadonlyArray<number>;
  sections: ReadonlyArray<CircleSectionMarker>;
  variant: Variant;
  /**
   * Stable seed for the PRNG (typically `chart.slug`). Same seed → same
   * layout, so a chart's generated positions are deterministic across
   * environments.
   */
  seed: string;
  /**
   * Optional id-prefix to namespace generated hit-object ids. Defaults
   * to `'c'`, producing `c-0`, `c-1`, ... — the chart slug already
   * disambiguates between maps so this rarely needs overriding.
   */
  idPrefix?: string;
}

/**
 * Generate a CircleHitObject sequence from a list of musical beats. The
 * sequence "flows" around the osu! playfield: each next position is
 * `stepBase + gap * stepGapScale` units away from the previous, in a
 * direction that turns by `angleDelta ± angleJitter` radians. When the
 * computed position lands outside the safe inner playfield rectangle we
 * reflect the angle so the next step bounces back inward — yielding
 * paths that snake organically without ever clipping the boundary.
 *
 * Combo grouping: every {@link COMBO_LENGTH} circles or any gap >
 * {@link COMBO_GAP_BEATS} starts a new combo and rotates through the
 * 4-colour palette.
 */
export function layoutHitObjects({
  beats,
  sections,
  variant,
  seed,
  idPrefix = 'c',
}: LayoutHitObjectsOpts): CircleHitObject[] {
  const rng = mulberry32(hashSeed(`${seed}:${variant}`));
  const sortedBeats = [...beats].sort((a, b) => a - b);
  const out: CircleHitObject[] = [];

  // Start near the centre of the playfield with a randomised initial
  // angle so the first circle of each chart doesn't always anchor in
  // the same direction.
  let x = OSU_PLAYFIELD_WIDTH / 2;
  let y = OSU_PLAYFIELD_HEIGHT / 2;
  let angle = rng() * Math.PI * 2;

  let comboIndex = 0;
  let comboCount = 0;
  let prevBeat = -Infinity;

  for (let i = 0; i < sortedBeats.length; i++) {
    const beat = sortedBeats[i];
    const pattern = patternForBeat(sections, beat);
    const gapBeats = i === 0 ? 1 : beat - prevBeat;
    const step = pattern.stepBase + Math.min(4, gapBeats) * pattern.stepGapScale;
    angle += pattern.angleDelta + (rng() - 0.5) * pattern.angleJitter;

    let nextX = x + Math.cos(angle) * step;
    let nextY = y + Math.sin(angle) * step;

    // If the candidate slips outside the safe area, reflect the angle
    // off whichever axis was crossed and recompute. We retry up to a
    // handful of times because a single reflection can still land
    // outside on a corner; after that we just clamp and let the next
    // circle's angle delta get us back in.
    for (let r = 0; r < 4; r++) {
      const insideX = nextX >= PLAYFIELD_PAD && nextX <= OSU_PLAYFIELD_WIDTH - PLAYFIELD_PAD;
      const insideY = nextY >= PLAYFIELD_PAD && nextY <= OSU_PLAYFIELD_HEIGHT - PLAYFIELD_PAD;
      if (insideX && insideY) {
        break;
      }
      if (!insideX) {
        angle = Math.PI - angle;
      }
      if (!insideY) {
        angle = -angle;
      }
      nextX = x + Math.cos(angle) * step;
      nextY = y + Math.sin(angle) * step;
    }
    nextX = clamp(nextX, PLAYFIELD_PAD, OSU_PLAYFIELD_WIDTH - PLAYFIELD_PAD);
    nextY = clamp(nextY, PLAYFIELD_PAD, OSU_PLAYFIELD_HEIGHT - PLAYFIELD_PAD);

    const isNewCombo =
      comboCount === 0 ||
      comboCount >= COMBO_LENGTH ||
      beat - prevBeat >= COMBO_GAP_BEATS;
    if (isNewCombo) {
      comboIndex = (comboIndex + 1) % COMBO_PALETTE_SIZE;
      comboCount = 0;
    }
    comboCount += 1;

    out.push({
      id: `${idPrefix}-${i.toString().padStart(4, '0')}`,
      beat,
      x: Math.round(nextX),
      y: Math.round(nextY),
      comboIndex,
      newCombo: isNewCombo,
      kind: 'circle',
    });

    x = nextX;
    y = nextY;
    prevBeat = beat;
  }
  return out;
}

export interface CirclesChartInit {
  baseSlug: string;
  variant: CirclesVariant;
  title: string;
  description: string;
  bpm: number;
  difficulty: Difficulty;
  totalBeats: number;
  palette: ChartPalette;
  audio: ChartAudio;
  hitObjects: CircleHitObject[];
  /** Optional credit for the chart's note-pattern author (e.g. osu! mapper). */
  mapper?: string;
  /**
   * Mapper-defined combo-colour cycle imported from the source `.osu`
   * `[Colours]` section. When present and non-empty the renderer uses
   * this list (cycled by `comboIndex`) instead of its built-in 4-colour
   * fallback. See {@link CirclesChart.comboColours} for the full
   * contract; we drop the field rather than persist an empty array
   * because validation treats `[]` and `undefined` identically and the
   * chart JSON stays cleaner without it.
   */
  comboColours?: string[];
  /**
   * Beat ranges where the source map flagged kiai mode active. See
   * {@link CirclesChart.kiaiSections}. Empty arrays are dropped
   * before the chart is constructed (validator rejects an explicit
   * empty array).
   */
  kiaiSections?: KiaiSection[];
  /**
   * `[Difficulty].SliderMultiplier` from the source map. See
   * {@link CirclesChart.sliderMultiplier}. Required (paired with
   * sliderTickRate) when any hit object is a slider; the validator
   * checks the pair-or-neither contract.
   */
  sliderMultiplier?: number;
  /**
   * `[Difficulty].SliderTickRate` from the source map. See
   * {@link CirclesChart.sliderTickRate}. Required (paired with
   * sliderMultiplier) when any hit object is a slider.
   */
  sliderTickRate?: number;
  /**
   * Skin sample-set bank for imported hitsound additions. See
   * {@link CirclesChart.hitSampleSet}. Dropped when `'normal'` (the
   * default) so the chart JSON stays clean.
   */
  hitSampleSet?: HitSampleSet;
}

/**
 * Assemble a CirclesChart from its parts. Centralised so every builtin
 * chart wires identical approach/circle parameters per variant — if we
 * later tweak the difficulty curve, only this helper changes.
 */
export function buildCirclesChart({
  baseSlug,
  variant,
  title,
  description,
  bpm,
  difficulty,
  totalBeats,
  palette,
  audio,
  hitObjects,
  mapper,
  comboColours,
  kiaiSections,
  sliderMultiplier,
  sliderTickRate,
  hitSampleSet,
}: CirclesChartInit): CirclesChart {
  const tuning = CIRCLES_VARIANT_TUNING[variant];
  const slug = buildCirclesVariantSlug(baseSlug, variant);
  // approachBeats is required by the chart schema for legacy reasons;
  // derive it from the BPM-independent approachMs so both representations
  // stay consistent. The engine reads `chart.approachMs` first when
  // present (see `buildCirclesEngineConfig`).
  const approachBeats = bpm > 0 ? (tuning.approachMs * bpm) / 60_000 : 0;
  // Slider tuning is paired: emit both or neither. The chart validator
  // rejects partial pairs (sliderMultiplier without sliderTickRate or
  // vice-versa), so we drop the whole pair rather than persist a
  // half-set object that would fail validation downstream.
  const hasSliderTuning =
    sliderMultiplier !== undefined && sliderTickRate !== undefined;
  return {
    mode: 'circles',
    id: slug,
    slug,
    title,
    description,
    bpm,
    difficulty,
    variant,
    totalBeats,
    palette,
    audio,
    hitObjects,
    approachMs: tuning.approachMs,
    approachBeats,
    circleRadius: tuning.circleRadius,
    perfectWindowMs: PERFECT_WINDOW_MS * tuning.windowScale,
    goodWindowMs: GOOD_WINDOW_MS * tuning.windowScale,
    okWindowMs: OK_WINDOW_MS * tuning.windowScale,
    ...(mapper !== undefined ? { mapper } : {}),
    ...(comboColours && comboColours.length > 0 ? { comboColours } : {}),
    ...(kiaiSections && kiaiSections.length > 0 ? { kiaiSections } : {}),
    ...(hasSliderTuning ? { sliderMultiplier, sliderTickRate } : {}),
    ...(hitSampleSet && hitSampleSet !== 'normal' ? { hitSampleSet } : {}),
  };
}

/**
 * One musical region of a chart with the rule for which onsets become
 * hit circles inside it. Mirrors the section + select-beats pattern
 * Pulse builtins use, so each Circles file reads as the spatial twin of
 * its Pulse counterpart with identical phrasing.
 */
export interface CirclesSectionSpec extends CircleSectionMarker {
  selectOpts: SelectBeatsOpts;
}

export interface CirclesChartSpec {
  baseSlug: string;
  baseTitle: string;
  description: string;
  difficulty: Difficulty;
  totalBeats: number;
  palette: ChartPalette;
  audio: ChartAudio;
  onsets: RawOnsetsFile;
  sections: ReadonlyArray<CirclesSectionSpec>;
}

/**
 * One-shot builder: takes a {@link CirclesChartSpec} (the constants of
 * a song) and produces a fully populated {@link CirclesChart} for the
 * requested variant. Each builtin file calls this three times — once
 * per variant — so the song's section structure and beat selection
 * rules live in one place.
 */
export function chartFromSpec(spec: CirclesChartSpec, variant: Variant): CirclesChart {
  const sectionMarkers: CircleSectionMarker[] = spec.sections.map((s) => ({
    startBeat: s.startBeat,
    endBeat: s.endBeat,
    kind: s.kind,
  }));

  const beatLists: number[][] = [];
  for (const section of spec.sections) {
    beatLists.push(
      variantSelectBeats(variant, spec.onsets, {
        ...section.selectOpts,
        fromBeat: section.selectOpts.fromBeat ?? section.startBeat,
        untilBeat: section.selectOpts.untilBeat ?? section.endBeat,
      }),
    );
  }
  // Dedup + sort across sections so a beat that lands on a section
  // boundary doesn't render two stacked circles.
  const sortedBeats = Array.from(new Set(beatLists.flat())).sort((a, b) => a - b);

  const hitObjects = layoutHitObjects({
    beats: sortedBeats,
    sections: sectionMarkers,
    variant,
    seed: spec.baseSlug,
  });

  return buildCirclesChart({
    baseSlug: spec.baseSlug,
    variant,
    title: buildVariantTitle(spec.baseTitle, variant),
    description: spec.description,
    bpm: spec.audio.bpm,
    difficulty: spec.difficulty,
    totalBeats: spec.totalBeats,
    palette: spec.palette,
    audio: spec.audio,
    hitObjects,
  });
}
